import { IEventDefinition } from "@highway/mbs-analytics";

export const appEvents: Record<string, IEventDefinition> = {
    // Cost of Waiting events
    cow_step1_submit: {
        description: "Fires when the user clicks 'Next' on Step 1 (after entering property info).",
        mappings: {
            mixpanel: "cow_step1_submit",
        },
    },
    cow_step1_submit_success: {
        description: "Fires when Step 1 is successfully completed.",
        mappings: {
            mixpanel: "cow_step1_submit_success",
        },
    },
    cow_step2_submit: {
        description: "Fires when the user clicks 'Next' on Step 2 (after entering loan info).",
        mappings: {
            mixpanel: "cow_step2_submit",
        },
    },
    cow_step2_submit_success: {
        description: "Fires when Step 2 is successfully completed.",
        mappings: {
            mixpanel: "cow_step2_submit_success",
        },
    },
    // Amortization events
    amortization_calculate_click: {
        description: "Fires when the user clicks 'Calculate' after entering inputs.",
        mappings: {
            mixpanel: "amortization_calculate_click",
        },
    },
    amortization_calculate_success: {
        description: "Fires when the report is successfully generated.",
        mappings: {
            mixpanel: "amortization_calculate_success",
        },
    },
    // Equity Gained events
    equity_gained_calculate_click: {
        description: "Fires when the user clicks 'Calculate' after entering inputs.",
        mappings: {
            mixpanel: "equity_gained_calculate_click",
        },
    },
    equity_gained_calculate_success: {
        description: "Fires when the report is successfully generated.",
        mappings: {
            mixpanel: "equity_gained_calculate_success",
        },
    },
    // Loan Comparison Purchase events
    lc_purchase_step1_submit: {
        description: "Fires when the user clicks 'Next' on Step 1.",
        mappings: {
            mixpanel: "lc_purchase_step1_submit",
        },
    },
    lc_purchase_step1_submit_success: {
        description: "Fires when Step 1 is successfully completed.",
        mappings: {
            mixpanel: "lc_purchase_step1_submit_success",
        },
    },
    // Loan Comparison events
    add_new_loan_click: {
        description: "Fires when the user clicks 'Add New Loan'.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_add_new_loan_click`,
        },
    },
    select_loan_method_optimal_blue: {
        description: "Fires when the user selects 'Optimal Blue' as the loan entry method.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_select_loan_method_optimal_blue`,
        },
    },
    select_loan_method_polly: {
        description: "Fires when the user selects 'Polly' as the loan entry method.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_select_loan_method_polly`,
        },
    },
    select_loan_method_manual: {
        description: "Fires when the user selects 'Manual' as the loan entry method.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_select_loan_method_manual`,
        },
    },
    optimal_blue_drawer_open: {
        description: "Fires when the Optimal Blue drawer opens.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_optimal_blue_drawer_open`,
        },
    },
    optimal_blue_next_click: {
        description: "Fires when the user clicks 'Next' after entering OB loan info.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_optimal_blue_next_click`,
        },
    },
    optimal_blue_select_loans: {
        description:
            "Fires when loans are selected for comparison. Properties: selected_count (number of loans selected)",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_optimal_blue_select_loans`,
        },
    },
    polly_drawer_open: {
        description: "Fires when the Polly drawer opens.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_polly_drawer_open`,
        },
    },
    polly_next_click: {
        description: "Fires when the user clicks 'Next' after entering Polly loan info.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_polly_next_click`,
        },
    },
    polly_select_loans: {
        description:
            "Fires when loans are selected for comparison. Properties: selected_count (number of loans selected)",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_polly_select_loans`,
        },
    },
    manual_add_click: {
        description: "Fires when the user clicks 'Add' after entering manual loan info.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_manual_add_click`,
        },
    },
    add_to_report_click: {
        description: "Fires when a user clicks 'Add to Report' after selecting at least one loan.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_add_to_report_click`,
        },
    },
    reinvestment_page_view: {
        description:
            "Fires when the user opens the Reinvestment Strategy modal — whether by clicking “Add,” “Edit,” or “View.”",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_reinvestment_page_view`,
        },
    },
    reinvestment_strategy_add: {
        description: "Fires when the user creates and saves a new Reinvestment Strategy for the first time.",
        mappings: {
            mixpanel: (context) => `${context?.eventPrefix}_reinvestment_strategy_add`,
        },
    },
    // Loan Compare Refinance Events
    lc_refi_step1_submit: {
        description: "Fires when the user clicks 'Next' on Step 1.",
        mappings: {
            mixpanel: "lc_refi_step1_submit",
        },
    },
    lc_refi_step1_submit_success: {
        description: "Fires when Step 1 is successfully completed.",
        mappings: {
            mixpanel: "lc_refi_step1_submit_success",
        },
    },
    lc_refi_step2_submit: {
        description: "Fires when the user clicks 'Next' on Step 2.",
        mappings: {
            mixpanel: "lc_refi_step2_submit",
        },
    },
    lc_refi_step2_submit_success: {
        description: "Fires when Step 2 is successfully completed.",
        mappings: {
            mixpanel: "lc_refi_step2_submit_success",
        },
    },
    // Strike Rate events
    strike_rate_notifications_toggle: {
        description:
            "Fires when a user toggles the Notifications button in the top right. Properties: toggle_state (on/off).",
        mappings: {
            mixpanel: "strike_rate_notifications_toggle",
        },
    },
    strike_rate_target_hits_edit_submit: {
        description:
            "Fires when a user submits edits to the strike rate target hits fields. Properties: fields_edited (early payoff period, below 350k, 350k - 766k, above 766k).",
        mappings: {
            mixpanel: "strike_rate_target_hits_edit_submit",
        },
    },
    strike_rate_transaction_add: {
        description:
            "Fires when a user manually adds a new transaction. Properties: Details of added fields/sections.",
        mappings: {
            mixpanel: "strike_rate_transaction_add",
        },
    },
    strike_rate_transaction_edit_submit: {
        description:
            "Fires when a user submits edits to any prepopulated transactions. Properties: fields_edited (strike rate, loan amount, interest rate, loan date, loan type).",
        mappings: {
            mixpanel: "strike_rate_transaction_edit_submit",
        },
    },
    strike_rate_transaction_delete: {
        description:
            "Fires when a user deletes a prepopulated transaction. Properties: Details of deleted transactions.",
        mappings: {
            mixpanel: "strike_rate_transaction_delete",
        },
    },
    strike_rate_refi_created: {
        description: "Fires when a user successfully creates a Refi Comparison report.",
        mappings: {
            mixpanel: "strike_rate_refi_created",
        },
    },
    dc_refi_step1_submit: {
        description: 'Fires when the user clicks "Next" after entering debt info.',
        mappings: {
            mixpanel: "dc_refi_step1_submit",
        },
    },
    dc_refi_step1_submit_success: {
        description: "Fires when Step 1 is successfully completed.",
        mappings: {
            mixpanel: "dc_refi_step1_submit_success",
        },
    },
    dc_refi_step2_submit: {
        description: 'Fires when the user clicks "Next" after entering existing loan details.',
        mappings: {
            mixpanel: "dc_refi_step2_submit",
        },
    },
    dc_refi_step2_submit_success: {
        description: "Fires when Step 2 is successfully completed.",
        mappings: {
            mixpanel: "dc_refi_step2_submit_success",
        },
    },
    dc_refi_step3_submit: {
        description: 'Fires when the user clicks "Next" after entering proposed loan details.',
        mappings: {
            mixpanel: "dc_refi_step3_submit",
        },
    },
    dc_refi_step3_submit_success: {
        description: "Fires when Step 3 is successfully completed.",
        mappings: {
            mixpanel: "dc_refi_step3_submit_success",
        },
    },
    dc_moveup_step1_submit: {
        description: 'Fires when the user clicks "Next" after entering debt info.',
        mappings: {
            mixpanel: "dc_moveup_step1_submit",
        },
    },
    dc_moveup_step1_submit_success: {
        description: "Fires when Step 1 is successfully completed.",
        mappings: {
            mixpanel: "dc_moveup_step1_submit_success",
        },
    },
    dc_moveup_step2_submit: {
        description: 'Fires when the user clicks "Next" after entering existing loan details.',
        mappings: {
            mixpanel: "dc_moveup_step2_submit",
        },
    },
    dc_moveup_step2_submit_success: {
        description: "Fires when Step 2 is successfully completed.",
        mappings: {
            mixpanel: "dc_moveup_step2_submit_success",
        },
    },
    dc_moveup_step3_submit: {
        description: 'Fires when the user clicks "Next" after entering proposed loan details.',
        mappings: {
            mixpanel: "dc_moveup_step3_submit",
        },
    },
    dc_moveup_step3_submit_success: {
        description: "Fires when Step 3 is successfully completed.",
        mappings: {
            mixpanel: "dc_moveup_step3_submit_success",
        },
    },
    dc_home_purchase_step1_submit: {
        description: 'Fires when the user clicks "Next" after entering debt info.',
        mappings: {
            mixpanel: "dc_home_purchase_step1_submit",
        },
    },
    dc_home_purchase_step1_submit_success: {
        description: "Fires when Step 1 is successfully completed.",
        mappings: {
            mixpanel: "dc_home_purchase_step1_submit_success",
        },
    },
    dc_home_purchase_step2_submit: {
        description: 'Fires when the user clicks "Next" after entering existing loan details.',
        mappings: {
            mixpanel: "dc_home_purchase_step2_submit",
        },
    },
    dc_home_purchase_step2_submit_success: {
        description: "Fires when Step 2 is successfully completed.",
        mappings: {
            mixpanel: "dc_home_purchase_step2_submit_success",
        },
    },
    dc_home_purchase_step3_submit: {
        description: 'Fires when the user clicks "Next" after entering proposed loan details.',
        mappings: {
            mixpanel: "dc_home_purchase_step3_submit",
        },
    },
    dc_home_purchase_step3_submit_success: {
        description: "Fires when Step 3 is successfully completed.",
        mappings: {
            mixpanel: "dc_home_purchase_step3_submit_success",
        },
    },
};
